import React from 'react';

import { studioTheme, STUDIO_SERIF, STUDIO_MONO } from './studioTheme';
import {
  StudioWrap,
  SourceLogo,
  Stars,
  DateText,
  secondaryLine,
} from './StudioCardKit';
import { StudioBody, isMediaTestimonial, StudioMediaCard } from './StudioBody';
import { socialCardKeys } from '@/src/constants/socialcard.constant';

/**
 * Broadsheet (widget type 11)
 *
 * Magazine pull-quote: an oversized serif quotation mark in the wall's
 * quotation colour, the testimonial set in a serif, and the attribution in
 * small-caps mono under it. The serif is the identity of this type, so the
 * quote keeps it even when a wall font is chosen; the attribution follows the
 * wall font.
 */
const BroadsheetCard = (props) => {
  const { cardItem, socialCardType } = props;
  const t = studioTheme(props);

  const role = secondaryLine(cardItem);
  const link = cardItem && cardItem[socialCardKeys.messageLink];

  /* One body type scale per card: the text testimonial and the video/audio
     summary must read as the same widget. */
  const bodyTextStyle = { fontFamily: STUDIO_SERIF, fontSize: t.smallSize ? 17 : 19, lineHeight: 1.4 };

  /* A video or audio testimonial IS the card - the player already carries
     the name and work title, so none of the chrome below is drawn. Only the
     summary keeps this widget's look, as the panel it extends into. */
  if (isMediaTestimonial(cardItem, socialCardType)) {
    return (
      <StudioMediaCard
        cardItem={cardItem}
        socialCardType={socialCardType}
        t={t}
        enclosure={{
          border: `1px solid ${t.line}`,
          borderRadius: 8,
          background: t.surface,
          /* the wall's custom card colour wins, as it does on the text card */
          ...(t.cardBackgroundObject || {}),
        }}
        summaryStyle={bodyTextStyle}
      />
    );
  }

  return (
    <StudioWrap>
      <div
        style={{
          position: 'relative',
          borderRadius: 8,
          padding: 22,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          border: `1px solid ${t.line}`,
          background: t.surface,
          ...(t.cardBackgroundObject || {}),
        }}
      >

        <span
          aria-hidden="true"
          style={{
            fontFamily: STUDIO_SERIF,
            fontSize: 56,
            lineHeight: 0.5,
            color: t.brand,
            height: 22,
            display: 'block',
          }}
        >
          &#8220;
        </span>

        <StudioBody
          t={t}
          cardItem={cardItem}
          socialCardType={socialCardType}
          textStyle={bodyTextStyle}
        />

        <Stars t={t} cardItem={cardItem} size={13} />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            minWidth: 0,
          }}
        >
          <div
            className={t.fontClass}
            style={{
              fontFamily: t.fontStyle ? undefined : STUDIO_MONO,
              fontSize: 11,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: t.muted,
              minWidth: 0,
            }}
          >
            <a
              href={link || undefined}
              target={link ? '_blank' : '_self'}
              rel="noreferrer noopener"
              style={{ color: t.ink, fontWeight: 500, textDecoration: 'none' }}
            >
              {cardItem && cardItem[socialCardKeys.authorName]}
            </a>
            {role ? ` — ${role}` : null}
          </div>
          <SourceLogo cardItem={cardItem} />
        </div>

        <DateText t={t} cardItem={cardItem} />
      </div>
    </StudioWrap>
  );
};

export default BroadsheetCard;
